import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Shield, ChevronDown, ChevronUp, X } from 'lucide-react';
import SafetyTip from './SafetyTip';
import { 
  SAFETY_TIPS, 
  SAFETY_TIP_CATEGORIES, 
  CONTEXTUAL_SAFETY_TIPS,
  SAFETY_ALERTS 
} from '../../utils/constants/safetyTips';

const theme = {
  colors: {
    primary: '#3b82f6',
    primaryLight: '#eff6ff',
    textHeading: '#0f172a',
    textBody: '#374151',
    textMuted: '#6b7280',
    background: '#ffffff',
    border: '#e5e7eb',
  },
  shadows: {
    sm: '0 1px 2px rgba(15, 23, 42, 0.06)',
    md: '0 4px 12px rgba(15, 23, 42, 0.08)',
  },
  radius: '12px',
};

const PanelContainer = styled.div`
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius};
  box-shadow: ${theme.shadows.md};
  overflow: hidden;
  margin-bottom: 20px;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, ${theme.colors.primaryLight} 0%, #f8fafc 100%);
  border-bottom: 1px solid ${theme.colors.border};
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, ${theme.colors.primaryLight} 0%, #f1f5f9 100%);
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderIcon = styled.div`
  width: 32px;
  height: 32px;
  background: ${theme.colors.primary};
  color: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderText = styled.div``;

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${theme.colors.textHeading};
`;

const HeaderSubtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${theme.colors.textMuted};
`;

const CollapseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.textMuted};
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(59, 130, 246, 0.1);
    color: ${theme.colors.primary};
  }
`;

const PanelContent = styled.div`
  padding: ${({ $isExpanded }) => $isExpanded ? '20px' : '0'};
  max-height: ${({ $isExpanded }) => $isExpanded ? '600px' : '0'};
  overflow-y: auto;
  transition: all 0.3s ease;
  background: #fafbfc;
`;

const TipsGrid = styled.div`
  display: grid;
  gap: 12px;
`;

const CategorySection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CategoryTitle = styled.h4`
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.textHeading};
  padding-bottom: 8px;
  border-bottom: 1px solid ${theme.colors.border};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AlertsSection = styled.div`
  margin-bottom: 20px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${theme.colors.textMuted};
  font-size: 14px;
`;

const SafetyTipsPanel = ({ 
  context = 'general', 
  showAlerts = true, 
  defaultExpanded = false,
  onDismissAlert 
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());

  // Load dismissed alerts from localStorage
  useEffect(() => {
    const dismissed = localStorage.getItem('dismissedSafetyAlerts');
    if (dismissed) {
      setDismissedAlerts(new Set(JSON.parse(dismissed)));
    }
  }, []);

  const handleDismissAlert = (alertId) => {
    const newDismissed = new Set([...dismissedAlerts, alertId]);
    setDismissedAlerts(newDismissed);
    localStorage.setItem('dismissedSafetyAlerts', JSON.stringify([...newDismissed]));
    
    if (onDismissAlert) {
      onDismissAlert(alertId);
    }
  };

  const getContextualTips = () => {
    switch (context) {
      case 'posting':
        return CONTEXTUAL_SAFETY_TIPS.POSTING_TASK;
      case 'offering':
        return CONTEXTUAL_SAFETY_TIPS.MAKING_OFFER;
      case 'messaging':
        return CONTEXTUAL_SAFETY_TIPS.MESSAGING;
      case 'first-time':
        return CONTEXTUAL_SAFETY_TIPS.FIRST_TIME_USER;
      default:
        return [];
    }
  };

  const getRelevantAlerts = () => {
    if (!showAlerts) return [];
    
    return SAFETY_ALERTS.HIGH_PRIORITY.filter(alert => {
      if (dismissedAlerts.has(alert.id)) return false;
      if (!alert.showOn) return true;
      
      const contextMap = {
        'posting': 'task-details',
        'offering': 'task-details', 
        'messaging': 'messaging',
        'acceptance': 'task-acceptance'
      };
      
      return alert.showOn.includes(contextMap[context] || 'general');
    });
  };

  const contextualTips = getContextualTips();
  const relevantAlerts = getRelevantAlerts();
  const hasContent = contextualTips.length > 0 || relevantAlerts.length > 0;

  const getTipCount = () => {
    return contextualTips.length + relevantAlerts.length;
  };

  return (
    <PanelContainer>
      <PanelHeader onClick={() => setIsExpanded(!isExpanded)}>
        <HeaderContent>
          <HeaderIcon>
            <Shield size={18} />
          </HeaderIcon>
          <HeaderText>
            <HeaderTitle>Safety Tips</HeaderTitle>
            <HeaderSubtitle>
              {getTipCount()} {getTipCount() === 1 ? 'tip' : 'tips'} for staying safe
            </HeaderSubtitle>
          </HeaderText>
        </HeaderContent>
        <CollapseButton>
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </CollapseButton>
      </PanelHeader>

      <PanelContent $isExpanded={isExpanded}>
        {hasContent ? (
          <TipsGrid>
            {/* Priority Alerts */}
            {relevantAlerts.length > 0 && (
              <AlertsSection>
                {relevantAlerts.map(alert => (
                  <SafetyTip
                    key={alert.id}
                    tip={alert}
                    type={alert.type}
                    dismissible={alert.dismissible}
                    onDismiss={handleDismissAlert}
                  />
                ))}
              </AlertsSection>
            )}

            {/* Contextual Tips */}
            {contextualTips.length > 0 && (
              <CategorySection>
                {context !== 'general' && (
                  <CategoryTitle>
                    {context === 'posting' && 'Posting Tasks Safely'}
                    {context === 'offering' && 'Making Safe Offers'}
                    {context === 'messaging' && 'Safe Communication'}
                    {context === 'first-time' && 'Getting Started Safely'}
                  </CategoryTitle>
                )}
                {contextualTips.map(tip => (
                  <SafetyTip
                    key={tip.id}
                    tip={tip}
                    type="info"
                    showPriority={true}
                  />
                ))}
              </CategorySection>
            )}
          </TipsGrid>
        ) : (
          <EmptyState>
            No safety tips available for this context.
          </EmptyState>
        )}
      </PanelContent>
    </PanelContainer>
  );
};

export default SafetyTipsPanel;